/* Génère des questions de quiz pour les modules Harakat, Formes et
   Ligatures — entièrement côté client, à partir du contenu statique de
   alphabetModules.js. Pas d'appel backend : le contenu de ces modules
   est fixe et connu à l'avance.

   Chaque question produite a exactement la forme attendue par Quiz.jsx
   (char, prompt, options, answer, explain, eyebrow, key, skill), donc
   elle traverse le même pipeline que les questions du module 1 —
   enregistrement des réponses et suivi de maîtrise inclus. */

import { t } from "@/i18n";

// Quel champ interroger, et comment formuler la question, pour chaque
// module et chaque mode de réglage. Les labels de mode dans
// alphabetModules.js ("La lettre"/"La position", "La lecture"/"Le
// sens"...) ont été choisis pour correspondre exactement à ces paires.
const FIELD_MAP = {
  harakat: {
    name: {
      field: "name",
      promptKey: "alphabetquiz.nom_signe",
      eyebrowKey: "alphabetquiz.obj_reconnaître_nom",
    },
    sound: {
      field: "sound",
      promptKey: "alphabetquiz.son_signe",
      eyebrowKey: "alphabetquiz.obj_reconnaître_son",
    },
  },
  formes: {
    name: {
      field: "letterName",
      promptKey: "alphabetquiz.lettre_est_ce",
      eyebrowKey: "alphabetquiz.obj_reconnaître_lettre",
    },
    sound: {
      field: "position",
      promptKey: "alphabetquiz.position_mot",
      eyebrowKey: "alphabetquiz.obj_reconnaître_position",
    },
  },
  ligatures: {
    name: {
      field: "name",
      promptKey: "alphabetquiz.lit_mot",
      eyebrowKey: "alphabetquiz.obj_lire_mot",
    },
    sound: {
      field: "sound",
      promptKey: "alphabetquiz.signifie_mot",
      eyebrowKey: "alphabetquiz.obj_trouver_sens",
    },
  },
};

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

// Choix multiple : jusqu'à 4 options, réduit automatiquement si le champ
// ciblé n'a pas assez de valeurs distinctes dans le module (ex. la
// "position" n'a que 4 valeurs possibles en tout).
function buildOptions(correct, universe, field, maxOptions = 4) {
  const uniques = [
    ...new Set(
      universe.map((x) => x[field]).filter((v) => v != null && v !== correct),
    ),
  ];
  const nWrong = Math.min(maxOptions - 1, uniques.length);
  const wrong = shuffle(uniques).slice(0, nWrong);
  const options = shuffle([correct, ...wrong]);
  return { options, answer: options.indexOf(correct) };
}

/**
 * @param mod        entrée de ALPHABET_MODULES (harakat / formes / ligatures)
 * @param groups     mod.groups (toutes les familles du module)
 * @param selectedIds ids des groupes cochés par l'utilisateur
 * @param mode       "both" | "name" | "sound"
 * @param count      nombre de questions désirées
 * @param pickedChars Set optionnel de chars choisis à la main — quand
 *                    fourni et non vide, remplace le filtrage par
 *                    groupes (mais les distracteurs restent tirés de
 *                    tout le module, pour garder des choix variés).
 * @returns un tableau de questions prêtes pour Quiz.jsx, ou [] si la
 *          sélection est vide ou le module non pris en charge.
 */
export function buildModuleQuestions(
  mod,
  groups,
  selectedIds,
  mode,
  count,
  pickedChars = null,
) {
  const map = FIELD_MAP[mod.slug];
  if (!map) return [];

  const universe = groups.flatMap((g) => g.letter_details || []);
  const pool =
    pickedChars && pickedChars.size > 0
      ? universe.filter((item) => pickedChars.has(item.char))
      : groups
          .filter((g) => selectedIds.includes(g.id))
          .flatMap((g) => g.letter_details || []);
  if (!pool.length) return [];

  // Types à générer : alternance stricte pour "both", fixe sinon.
  const types = Array.from({ length: count }, (_, i) =>
    mode === "both" ? (i % 2 === 0 ? "name" : "sound") : mode,
  );

  // On tire les éléments interrogés en cyclant sur des mélanges
  // successifs du pool, pour couvrir toute la sélection avant de répéter.
  const items = [];
  while (items.length < count) items.push(...shuffle(pool));
  items.length = count;

  return items.map((item, i) => {
    const type = types[i];
    const cfg = map[type];
    const correct = item[cfg.field];
    const { options, answer } = buildOptions(correct, universe, cfg.field);
    return {
      char: item.char,
      prompt: t(cfg.promptKey),
      options,
      answer,
      explain: item.hint || "",
      eyebrow: t(cfg.eyebrowKey),
      key: `${mod.slug}:${item.char}`,
      skill: "alphabet",
    };
  });
}
